import { X, Check, Loader2 } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import {
  useAchievements,
  type Achievement,
  ACHIEVEMENT_DEFINITIONS,
} from "../hooks/useAchievements";
import { useAuth } from "../contexts/AuthContext";

interface AchievementsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AchievementsModal({ isOpen, onClose }: AchievementsModalProps) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { achievements, loading } = useAchievements(user?.id);

  if (!isOpen) return null;

  // For non-logged-in users, show all achievements with zero progress
  const displayAchievements: Achievement[] = user
    ? achievements
    : Object.values(ACHIEVEMENT_DEFINITIONS).map((def) => ({
        key: def.key,
        emoji: def.emoji,
        labelKey: def.labelKey,
        descriptionKey: def.descriptionKey,
        earned: false,
        progress: 0,
        target: def.target,
      }));

  const isLoading = user ? loading : false;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-primary to-primary-dark p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-1">
                {t("achievement.badgeGuide")}
              </h2>
              <p className="text-white/90 text-sm">
                {t("achievement.howToEarn")}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-smooth"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : (
            <div className="grid gap-3">
              {displayAchievements.map((achievement) => (
                <AchievementCard
                  key={achievement.key}
                  achievement={achievement}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Color schemes matching the exact specifications
const getAchievementColors = (key: string, earned: boolean) => {
  const schemes: Record<
    string,
    {
      cardBg: string;
      iconBg: string;
      border: string;
      unlockColor: string;
      progressBar: string;
      progressBg: string;
    }
  > = {
    expertCAF: {
      cardBg: "bg-[#fcd116]/10",
      iconBg: "bg-[#fcd116]/20",
      border: "border-[#fcd116]/20",
      unlockColor: "text-[#fcd116]",
      progressBar: "bg-[#fcd116]",
      progressBg: "bg-[#fcd116]/20",
    },
    perfect10: {
      cardBg: "bg-[#00864a]/10",
      iconBg: "bg-[#00864a]/20",
      border: "border-[#00864a]/20",
      unlockColor: "text-[#00864a]",
      progressBar: "bg-[#00864a]",
      progressBg: "bg-[#00864a]/20",
    },
    boldMaster: {
      cardBg: "bg-gray-100",
      iconBg: "bg-gray-200",
      border: "border-gray-200",
      unlockColor: "text-[#475569]",
      progressBar: "bg-[#475569]",
      progressBg: "bg-gray-200",
    },
    streak5: {
      cardBg: "bg-[#d32f2f]/10",
      iconBg: "bg-[#d32f2f]/20",
      border: "border-[#d32f2f]/20",
      unlockColor: "text-[#d32f2f]",
      progressBar: "bg-[#d32f2f]",
      progressBg: "bg-[#d32f2f]/20",
    },
  };

  return schemes[key] || schemes.expertCAF;
};

function AchievementCard({ achievement }: { achievement: Achievement }) {
  const { t } = useLanguage();
  const progressPercentage = Math.min(
    (achievement.progress / achievement.target) * 100,
    100
  );

  const colors = getAchievementColors(achievement.key, achievement.earned);

  return (
    <div
      className={`flex gap-3 p-3 rounded-xl ${colors.cardBg} border ${colors.border}`}
    >
      {/* Icon - Circular background */}
      <div
        className={`w-12 h-12 rounded-full ${colors.iconBg} flex items-center justify-center text-2xl flex-shrink-0`}
      >
        {achievement.emoji}
      </div>

      {/* Content */}
      <div className="flex-1">
        <p className="text-sm mb-1">{t(achievement.labelKey)}</p>
        <p className="text-xs text-gray-600 mb-2">
          {t(achievement.descriptionKey)}
        </p>

        {/* Status or Progress */}
        {achievement.earned ? (
          <div className="flex items-center gap-2">
            <div
              className={`w-5 h-5 rounded-full ${colors.progressBar} flex items-center justify-center`}
            >
              <Check className="w-3 h-3 text-white" strokeWidth={3} />
            </div>
            <span className={`text-xs ${colors.unlockColor}`}>
              {t("achievement.unlocked") || "Unlocked"}
            </span>
          </div>
        ) : (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">
                {achievement.progress}/{achievement.target}
              </span>
              <span className={colors.unlockColor}>
                {Math.min(100, Math.round(progressPercentage))}%
              </span>
            </div>
            <div
              className={`h-2 ${colors.progressBg} rounded-full overflow-hidden`}
            >
              <div
                className={`h-full ${colors.progressBar} rounded-full transition-all`}
                style={{ width: `${Math.min(100, progressPercentage)}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
